import { get } from '@ember/helper';
import { type Registry as Services, service } from '@ember/service';
import Component from '@glimmer/component';
import { PrismBanner, PrismCard, PrismIcon } from '@mylighthouse/prism-ember';
import EmberTooltip from '@mylighthouse/prism-ember/components/ember-tooltip';
import { t } from 'ember-intl';
import type { RateIndexesForCurrentSelectionPerSubmarketLevelBucket } from 'lumen/components/hotel/market-insight/cards/price-level-distribution';
import HotelMarketInsightChartsChartDemandLevelBuckets from 'lumen/components/hotel/market-insight/charts/chart-demand-level-buckets';
import HotelMarketInsightTablesTablePriceLevelDistributionDetails from 'lumen/components/hotel/market-insight/tables/table-price-level-distribution-details';
import type { Day } from 'lumen/models';

interface HotelMarketInsightCardsPriceLevelDistributionDetailsSignature {
  Args: {
    isLoadingSummariesOrDetail: boolean;
    rateIndexDataForCurrentSelection: unknown;
    rateIndexesForCurrentSelectionPerSubmarketLevelBucket: RateIndexesForCurrentSelectionPerSubmarketLevelBucket;
    selectedDayModel: Day;
    setHighlightedMarker: unknown;
  };
}

export default class HotelMarketInsightCardsPriceLevelDistributionDetails extends Component<HotelMarketInsightCardsPriceLevelDistributionDetailsSignature> {
  @service declare miHotels: Services['mi-hotels'];

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get model() {
    if (this.args.rateIndexDataForCurrentSelection) {
      // @ts-expect-error: Incorrect type
      // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-return
      return this.args.rateIndexDataForCurrentSelection.map((rateIndex) => {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        const {
          demandLevelColorScaleNumberDow,
          usualRateDow,
          formattedUsualRateDow,
          rate,
          formattedRate,
          formattedCancellableIcon,
          formattedRoomNameAndCancellableText,
          hasUsualRateDowValue,
          canShowFormattedCancellableText,
        } = rateIndex;

        // @ts-expect-error: Incorrect type
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
        const hotel = this.miHotels.gridResponseMapping[rateIndex.ourHotelId];
        // @ts-expect-error: Incorrect type
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        const isOwnHotel = rateIndex.ourHotelId === this.miHotels.ownOurHotelId;

        return {
          // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
          demandLevelColorScaleNumberDow,
          // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
          usualRateDow,
          // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
          formattedUsualRateDow,
          // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
          rate,
          // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
          formattedRate,
          // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
          formattedCancellableIcon,
          // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
          formattedRoomNameAndCancellableText,
          // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
          hotelId: rateIndex.ourHotelId,
          // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
          hotel,
          isOwnHotel,
          // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
          hasUsualRateDowValue,
          // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
          canShowFormattedCancellableText,
        };
      });
    }

    return [];
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get usualRateDow() {
    return this.args.selectedDayModel.date.format('ddd');
  }

  <template>
    {{! @glint-nocheck }}
    <PrismCard
      @contentLayout="noPadding"
      class="detail-pane-card price-level-distribution-details"
      data-test-id="market-insight-price-level-distribution-details"
    >
      <:header as |Header|>
        <div class="flex items-center gap-100">
          <Header.title>
            {{t "market_insight.price_level_distribution"}}
          </Header.title>
          <div>
            <PrismIcon @name="circle-information-outline" @size="extra-small" />
            <EmberTooltip @side="right" @tooltipClass="info-tooltip">
              <p class="text-200--bold">{{t
                  "market_insight.price_level_distribution"
                }}</p>
              <p class="paragraph-200--regular">{{t
                  "market_insight.price_level_distribution.tooltip"
                }}</p>
              &nbsp;
              <p class="text-200--bold">{{t "market_insight.usual_price"}}</p>
              <p class="paragraph-200--regular">{{t
                  "market_insight.price_level_distribution.tooltip.dow_price"
                }}</p>
            </EmberTooltip>
          </div>
        </div>
      </:header>

      <:content>
        <div class="price-level-distribution-details__content-with-margin">
          {{#if @selectedDayModel.outliers}}
            <div class="mb-200">
              <PrismBanner
                @bannerStyle="warning"
                @bannerTitle={{get
                  @selectedDayModel.outliers
                  "0.formattedValueString.short"
                }}
              />
            </div>
          {{/if}}

          <HotelMarketInsightChartsChartDemandLevelBuckets
            @isLoading={{@isLoadingSummariesOrDetail}}
            @rateIndexesForCurrentSelectionPerSubmarketLevelBucket={{@rateIndexesForCurrentSelectionPerSubmarketLevelBucket}}
            @selectedDayModel={{@selectedDayModel}}
          />
        </div>

        <div class="divider-line divider-line--no-margin-bottom mt-400"></div>
        <HotelMarketInsightTablesTablePriceLevelDistributionDetails
          @model={{this.model}}
          @setHighlightedMarker={{@setHighlightedMarker}}
          @usualRateDow={{this.usualRateDow}}
        />
      </:content>
    </PrismCard>
  </template>
}
